import {
  faqs,
  howItWorks,
  TCListItems,
  CheckoutTerms,
  AnalyticsHeader,
  RegistrationTerms,
  AdditionalCheckoutInputFields,
} from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/background.jpg";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/toronto-blue-jays-banner.png";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/Toronto_Blue_Jay_Primary_Logo.svg.png";

const config: TeamConfig = {
  teamSchema: "torontobluejays_flex",
  browserTitle: "Blue Jays Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Toronto Blue Jays",

  buckets: {
    enabled: true,
    prioritizeMinGameSelectionCount: true,
    prioritizeMaxGameSelectionCount: true,
    list: [
      {
        id: 2,
        min: 0,
        max: 2,
        remainingBucketLanguage: "",
        name: "Unlock exclusive access!",
        description:
          "Once you select 10 games, you unlock access to purchase Canada Day!",
      },
      {
        id: 1,
        min: 10,
        max: 19,
        isPrimary: true,
        remainingBucketLanguage: "",
        dontCloseWhenHitMin: true,
        scrollToTopWhenSelecting: true,
        name: "Select 10 games to build your Flex Pack!",
        description:
          "Once you select 10 games, you unlock access to purchase Canada Day!",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-ca-10001-bluejays-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: true,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 10,
    maxGameSelectionCount: 19,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,

    hideViewTicketsInThankYouPage: true,

    checkout: {
      totalAmountBreakdown: {
        isEnabled: true,
        totalAmountLabel: "Total Amount",
        ticketTotalLabel: "Tickets Total",
        ticketFeesLabel: "Ticket Fees",
        taxesLabel: "Taxes",
      },
    },
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-N8YFCZLYSZ",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/torontobluejays_flex/events_home",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/tbjrc/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: BackgroundImage,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    checkoutPage: {
      checkoutForm: {
        checkoutTermsAndConditionsSecondary: <AdditionalCheckoutInputFields />,
      },
    },

    unlockExclusiveAccessModal: {
      enabled: true,
      title: "Unlock exclusive access!",
      description:
        "Just a reminder that your Flex Pack gives you exclusive access to Canada Day. If you haven't added that event yet, be sure to do so now so you don't miss the action!",
      buttonText: "Add Games",
    },
  },

  colors: {
    primary: "#134A8E",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <TCListItems />,
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <RegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",

      inventoryCardPricePerSeatLabel: "Price per Seat (incl. taxes)",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Tickets will be available to be managed in your Toronto Blue Jays Account Manager on March 2.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
